import React, { useState, useEffect, useRef } from "react";
import "./ConnectForm.css";

const ConnectForm = () => {
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", location: "", isPilot: "", purpose: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [recaptchaId, setRecaptchaId] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const recaptchaRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    if (window.grecaptcha?.render) {
      setRecaptchaLoaded(true);
      setTimeout(renderRecaptcha, 100);
      return;
    }
    
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    
    window.onRecaptchaLoad = () => {
      setRecaptchaLoaded(true);
      renderRecaptcha();
    };
    
    script.onerror = () => setRecaptchaError(true);
    document.head.appendChild(script);

    return () => {
      if (recaptchaId !== null && window.grecaptcha?.reset) {
        try { window.grecaptcha.reset(recaptchaId); } catch (e) {}
      }
    };
  }, []);

  const renderRecaptcha = () => {
    if (window.grecaptcha?.render && recaptchaRef.current && recaptchaId === null) {
      try {
        const id = window.grecaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          size: 'normal',
          callback: (response) => setRecaptchaValue(response),
          'expired-callback': () => setRecaptchaValue(null),
          'error-callback': () => setRecaptchaError(true),
        });
        setRecaptchaId(id);
      } catch (error) {
        setRecaptchaError(true);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const isFieldActive = (fieldName) => focusedField === fieldName || formData[fieldName] !== '';

  const isFormValid = () => {
    const { name, phone, email } = formData;
    return name.trim() && phone.trim() && email.trim() && (recaptchaLoaded || recaptchaError);
  };

  const validateForm = () => {
    const { name, phone, email } = formData;
    if (!name.trim()) return "Name is required";
    if (!phone.trim()) return "Phone number is required";
    if (!email.trim()) return "Email is required";
    if (!recaptchaLoaded && !recaptchaError) return 'Please wait for security verification to load';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    await submitForm();
  };

  const submitForm = async () => {
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        operating_location: formData.location.trim(),
        drone_pilot_check: formData.isPilot === 'yes' ? 'Y' : formData.isPilot === 'no' ? 'N' : '',
        purpose: formData.purpose.trim(),
        status: "inactive",
        recaptcha_response: recaptchaValue || 'captcha_disabled'
      };

      const response = await fetch(`${process.env.REACT_APP_CMS_BASE_URL}/api/lets-connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFormData({ name: "", phone: "", email: "", location: "", isPilot: "", purpose: "" });
        if (window.grecaptcha && recaptchaId !== null) {
          window.grecaptcha.reset(recaptchaId);
          setRecaptchaValue(null);
        }
        setSuccess(true);
        setTimeout(() => setSuccess(false), 5000);
      } else {
        throw new Error(data.msg || 'Submission failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
      if (window.grecaptcha && recaptchaId !== null) {
        window.grecaptcha.reset(recaptchaId);
        setRecaptchaValue(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderField = (name, type = 'text', placeholder, label, required = false) => (
    <div className="form-group">
      {type === 'textarea' ? (
        <textarea
          name={name}
          rows="4"
          value={formData[name]}
          onChange={handleChange}
          onFocus={() => setFocusedField(name)}
          onBlur={() => setFocusedField(null)}
          disabled={loading}
          required={required}
          className="form-textarea"
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          onFocus={() => setFocusedField(name)}
          onBlur={() => setFocusedField(null)}
          disabled={loading}
          required={required}
          className="form-input"
          placeholder={placeholder}
        />
      )}
      <label className={`form-label ${isFieldActive(name) ? 'active' : ''}`}>
        {label}{required && <span className="required">*</span>}
      </label>
    </div>
  );

  return (
    <div className="connect-form-container">
      <form className="connect-form" onSubmit={handleSubmit}>
        {success && (
          <div className="submit-status">
            <p>✓ Thank you for reaching out. We will get back to you soon.</p>
          </div>
        )}
        
        {error && (
          <div className="submit-error">
            <p>⚠ {error}</p>
          </div>
        )}

        {renderField('name', 'text', 'Name', 'Name', true)}
        {renderField('phone', 'tel', 'Phone Number', 'Phone Number', true)}
        {renderField('email', 'email', 'Email address', 'Email address', true)}
        {renderField('location', 'text', 'Operating Location', 'Operating Location', false)}
        
        <div className="form-group radio-group">
          <label className="form-label radio-label">Are you a drone pilot?</label>
          <div className="radio-container">
            <label className="radio-option">
              <input
                type="radio"
                name="isPilot"
                value="yes"
                checked={formData.isPilot === 'yes'}
                onChange={handleChange}
                disabled={loading}
              />
              <span>Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="isPilot"
                value="no"
                checked={formData.isPilot === 'no'}
                onChange={handleChange}
                disabled={loading}
              />
              <span>No</span>
            </label>
          </div>
        </div>

        {renderField('purpose', 'textarea', 'Purpose/Application', 'Purpose/Application', false)}
        
        <div className="recaptcha-container">
          <div ref={recaptchaRef}></div>
          {!recaptchaLoaded && !recaptchaError && (
            <p className="captcha-loading">Loading security verification...</p>
          )}
        </div>
        
        <button 
          type="submit" 
          className="submit-button"
          disabled={loading || !isFormValid()}
        >
          {loading ? 'Submitting...' : 'Send'}
        </button>
      </form>
    </div>
  );
};

export default ConnectForm;